/*
 * state.js
 *
 * Single mutable state object shared across all modules. Mutate fields
 * directly (e.g. `state.person = 'lander'`); render.js re-renders the
 * UI from the new state on demand.
 *
 * `state.S` holds the same shape as the original artifact:
 *   sven, lander  → personal accounts
 *   samen         → shared household account
 *
 * Once Supabase wiring lands (migration plan step 6), the contents of
 * `state.S` get populated from a SELECT query at startup instead of
 * being hardcoded.
 */

const TODAY = new Date();
const S0    = initialSeed();

// Default-view = kalendermaand. De doorroll naar een nieuwe cyclus na de
// anker-dag gebeurt pas wanneer de gebruiker "Loon ontvangen" bevestigt
// (zie helpers.js → todayKey + de melding in panels.js → gatherNotifs).
// Tot dat moment blijft de viewed cycle gelijk aan de gated todayKey.
const initM = TODAY.getMonth();
const initY = TODAY.getFullYear();

export const state = {
  // TODAY wordt als getter blootgesteld zodat helpers altijd de actuele
  // datum krijgen, niet de bevroren module-load tijd. Voorkomt bugs als
  // de gebruiker de app over middernacht open laat staan of als entries
  // in een ander cycle-bucket landen dan de UI laat zien.
  get TODAY() { return new Date(); },
  vM: initM,
  vY: initY,
  // user = wie ben ik (default Sven). person = wiens data toon ik nu.
  // Kijker-modus = state.person !== state.user en !== 'samen'.
  user: 'sven',
  person: 'sven',
  // Mapping van interne slot-keys ('sven'/'lander'/'samen') naar de échte
  // weergavenamen (uit profile.display_name na hydrate). Tot dat hydrate
  // klaar is, vallen we terug op de gekapitaliseerde slot-key.
  displayNames: { sven: 'Sven', lander: 'Lander', samen: 'Samen' },
  editing: {},
  pendingAdd: null,
  modalOpen: false,
  modalDir: 'uit',          // 'uit' | 'in'
  modalRek: 'eigen',        // 'eigen' | 'samen' | 'spaar'
  // Begroot-toggle in de add-modal (Uit + eigen). 'begroot' = entry vult
  // de balk van een gekozen vast/var-item. 'niet' = off-plan, eet aan
  // het weekbedrag. modalBegrootItemKey is 'type:index', alleen relevant
  // bij 'begroot'. modalIsDePayment is alleen relevant voor niet-
  // maandelijkse items (sinking funds: vinkje = "dit reset de teller").
  modalBegrootMode: 'begroot',     // 'begroot' | 'niet'
  modalBegrootItemKey: null,
  modalIsDePayment: false,
  // Cat-keuze in de add-modal apart in state houden zodat buildBegrootSection
  // bij re-render altijd de juiste matches kan tonen — innerHTML wist anders
  // de <select>-waarde vóór de section getekend wordt.
  modalCat: null,
  modalSettingsOpen: false,
  // Welke "pagina" binnen instellingen actief is. Voorkomt dat je alle secties
  // tegelijk ziet — je drilt per item door en gaat terug via ‹.
  // 'menu' = hoofdmenu; 'user' / 'cashflow' / 'belasting' /
  // 'cats-uit' / 'cats-in' / 'cat' / 'extschulden' / 'gevarenzone'.
  settingsView: 'menu',
  // Add-item popup: vervangt de inline addform-toggles in vast/var/ink/dromen.
  // null = dicht; anders een config-object zoals { type: 'vast', person: 'sven' }.
  addItemModal: null,
  // FAB picker — kleine bottom-sheet voor de zwevende +-knop op context-tabs.
  // null = dicht; anders { kind: 'droom'|'inkomst'|'vast'|'var', label: '…' }.
  fabPicker: null,
  modalBespaarOpen: false,
  modalReflectOpen: false,  // Fase E: maandafsluit-reflectie modal
  modalReflectCycle: null,  // welke cycleKey wordt herzien
  // Lang-weg-popup: getriggerd door hydrate als last_seen_at ≥ 90 dagen oud
  // is. Vraagt of app_start_date geherset moet worden zodat "Rest vorige
  // maand" niet stale baseline toont.
  absencePopupOpen: false,
  absenceMonths: 0,
  editingCat: null,         // cat-id van categorie die nu bewerkt wordt
  activeTab: 'home',
  // Sub-tab binnen de "Uitgaven"-tab. Geldig: 'extra' / 'vast' / 'variabel'
  // voor persoon-modus, 'svast' / 'svar' voor samen. Wordt geset door
  // switchTab('uitgaven') (default uit TABS_P/TABS_S.defaultSub) en door
  // switchSubTab(); blijft anders bewaard zodat een tab-switch terug naar
  // dezelfde sub-keuze opent.
  activeSubTab: 'extra',
  // Meldingen-modus: aparte schermweergave waar de gebruiker enkel met
  // meldingen omgaat. Tab-bar + person-switcher zijn verborgen totdat
  // er op de accountnaam-knop geklikt wordt (= terug).
  notifsOpen: false,
  // Dropdown onder de accountnaam-knop in de header — kies welke Florijn
  // je bekijkt (eigen / samen / mate-shared).
  personMenuOpen: false,
  // Is er een medebewoner aangesloten op het huishouden? Gezet in hydrate
  // op basis van fetchHouseholdMembers (mateProfile != null). Vóór hydrate
  // null/false; gebruikt door de huishouden-sectie in instellingen om de
  // share-checkbox of de uitnodig-hint te tonen.
  mateConnected: false,
  // Heeft het huishouden een gezamenlijke rekening? Aangevinkt in
  // settings → 🏠 Huishouden. Wanneer uit:
  //   - Samen-item verdwijnt uit de persoon-dropdown
  //   - Gem-storting-notif fire-t niet
  //   - Auto-gezamenlijk vast-item van personen wordt verborgen
  // Bestaande samen-data blijft bewaard — gewoon visueel verborgen.
  hasSharedAccount: false,
  // Eerste-keer-tutorial. Gezet in hydrate op !profile.onboarding_done.
  // De tour zelf zit in lib/onboarding.js — main.js triggert 'm na de
  // eerste render. Vanuit settings → 👤 User opnieuw startbaar.
  onboardingOpen: false,
  // "Vergeet je bankapp"-modal: getoond zodra de gebruiker een notif op
  // 'auto' zet (gem-storting of sparen). Eenmalige reminder dat Florijn
  // dat bedrag nu zelf veronderstelt — bankapp ook bijwerken!
  modalAutoNoticeOpen: false,
  modalAutoNoticeKind: null,   // 'gem' | 'spaar'
  // "Deze maand niet sparen"-modal: financieel-coachende tussenstap die
  // probeert de gewoonte intact te houden. Twee views: 'tip' (uitleg +
  // kies) en 'custom' (kies een eigen bedrag voor deze maand).
  modalSpaarSkipOpen: false,
  modalSpaarSkipView: 'tip',   // 'tip' | 'custom'
  // Bij openReflect kiezen we max 2 random abonn-items om naar te vragen;
  // hun __id's lockten we hier vast zodat de selectie niet herrandomized
  // wordt op elke render. Wordt leeggemaakt op closeReflect/saveReflect.
  reflectionAbonnIds: [],
  S: S0
};

/**
 * Seed data lifted from florijn_v4.html. Keep this as the v0 fallback
 * so the app boots usable even before Supabase is wired. After the
 * Supabase migration this becomes empty defaults; user data comes
 * from the database.
 */
function initialSeed() {
  return {
    cats: initialCats(),

    // App-instellingen. appStartDate ('YYYY-MM-DD') definieert vanaf wanneer
    // de surplus-berekening valide is (cycles ervoor → geen surplus tonen).
    // Null = altijd surplus berekenen (assumes loon was er ook vorige maand).
    settings: {
      appStartDate: null,
      // Twee assen: mode (helderheid) en theme (kleur-palette). Elk thema
      // werkt in beide modi. Pride auto-trigger op IDAHOT/juni is afgezet
      // — de gebruiker kiest expliciet.
      mode: 'light',            // 'light' | 'dark'
      theme: 'default'          // 'default' | 'frietjes' | 'lowie' | 'pride'
    },

    sven: {
      shareWithMate: false,   // mag Lander mijn data inkijken (read-only)?
      gemAuto: false,
      spaarAuto: false,
      gemHandled: {},     // cycleKey → true (= gem-storting bevestigd voor die cyclus)
      spaarHandled: {},   // cycleKey → true (= sparen gestort/verdeeld voor die cyclus)
      loonHandled: {},    // cycleKey → true (= "Loon ontvangen?" bevestigd voor die cyclus). Gated todayKey blijft op de vorige cyclus tot deze gezet is.
      huidigSaldo: null,         // Fase C: huidig rekeningsaldo (€). null = nog niet ingesteld → cashflow-tegel verborgen.
      huidigSaldoUpdated: null,  // ISO 'YYYY-MM-DD' van de laatste update.
      pensioenActief: false,     // Fase D: opt-in. Off = geen pensioen-tegel in Dromen, geen settings-velden.
      pensioenGrens: 1050,       // Fase D: jaarlijkse fiscale grens BE pensioensparen. 1050 (30% voordeel) of 1350 (25%).
      belastingPct: 25,          // Fase D: default % te reserveren bij belastbare extra inkomst.
      cycleReviews: {},          // Fase E: cycleKey → { note, completedAt, skipped }.
      inkomsten: [
        { n: 'Loon',           a: 2288.09, f: 'm', cat: 'loon',         startDag: 25, anker: true  },
        { n: 'Maaltijdcheques', a: 64,      f: 'm', cat: 'maaltijdcheq', startDag: 15, anker: false }
      ],
      surplusHandled: {},
      vast: [
        { n: 'Gezamenlijke kosten', a: 0, f: 'm', cat: 'gezamenlijk', auto: true },
        { n: 'Huur Garage',         a: 65,     f: 'm', cat: 'wonen' },
        { n: 'Hospitalisatie',      a: 56.99,  f: 'm', cat: 'verzekeringen' },
        { n: 'ACV',                 a: 20.5,   f: 'm', cat: 'verzekeringen' },
        { n: 'Ziekenkas',           a: 103.8,  f: 'j', cat: 'gezondheid' },
        { n: 'Wegentax',            a: 180,    f: 'j', cat: 'vervoer' }
      ],
      var: [
        { n: 'Streamz',     a: 13.99, f: 'm', c: '#e91e8c', cat: 'abonn' },
        { n: 'Psycholoog',  a: 11,    f: 'm', c: '#27ae60', cat: 'gezondheid' },
        { n: 'Mubi',        a: 15,    f: 'm', c: '#8e44ad', cat: 'abonn' },
        { n: 'Claude AI',   a: 218,   f: 'j', c: '#5c6bc0', cat: 'abonn' },
        { n: 'Lowie',       a: 50,    f: 'm', c: '#2a9d5a', cat: 'huisdier' }
      ],
      // weekBudgets: catId → € per week. Cats hierin worden begroot
      // beschouwd (geen "is dit begroot?"-vraag) en krijgen een
      // weekwidget op Home + maandbudget-overschrijdings-notif.
      weekBudgets: { boodschappen: 45, uitgaan: 30 },
      hist: {},
      schulden: [],
      externeSchulden: [],
      dromen: [
        { n: 'Vakantie',  doel: 2000, gesp: 800,  pct: 60, c: '#2a9d5a' },
        { n: 'Noodpotje', doel: 3000, gesp: 1200, pct: 40, c: '#5c6bc0', isNoodfonds: true }
      ]
    },

    lander: {
      shareWithMate: false,
      gemAuto: false,
      spaarAuto: false,
      gemHandled: {},
      spaarHandled: {},
      loonHandled: {},
      huidigSaldo: null,
      huidigSaldoUpdated: null,
      pensioenActief: false,
      pensioenGrens: 1050,
      belastingPct: 25,
      cycleReviews: {},
      inkomsten: [],
      surplusHandled: {},
      vast: [
        { n: 'Gezamenlijke kosten', a: 0, f: 'm', cat: 'gezamenlijk', auto: true }
      ],
      var: [],
      weekBudgets: { boodschappen: 40, uitgaan: 25 },
      hist: {},
      schulden: [],
      externeSchulden: [],
      dromen: []
    },

    samen: {
      vast: [
        { n: 'Huur',                a: 1199,   f: 'm', c: '#5c6bc0', cat: 'wonen' },
        { n: 'Internet',            a: 66,     f: 'm', c: '#378ADD', cat: 'nutsvoorz' },
        { n: 'Water',               a: 110,    f: 'm', c: '#16a1b5', cat: 'nutsvoorz' },
        { n: 'Gas & elektriciteit', a: 144,    f: 'm', c: '#e67e22', cat: 'nutsvoorz' },
        { n: 'Brandverzekering',    a: 204.01, f: 'j', c: '#8e44ad', cat: 'verzekeringen' }
      ],
      var: [
        { n: 'June',   a: 69,  f: 'j', c: '#e91e8c', cat: 'vervoer' },
        { n: 'Buffer', a: 125, f: 'm', c: '#2a9d5a', cat: 'onverwacht' }
      ],
      hist: {}
    }
  };
}

/**
 * Initiele categorie-set. CRUD-baar via de Instellingen-pagina (punt 5).
 * Elke categorie heeft een vaste id (intern, niet zichtbaar voor user),
 * een label (zichtbaar), emoji + kleur (voor pie/legenda), en scope
 * (welke kant van de balans hij hoort). `locked` betekent dat de gebruiker
 * 'm niet kan verwijderen — voor "Gezamenlijke kosten" + "Extra sparen"
 * die elders speciaal gedrag triggeren.
 */
function initialCats() {
  return [
    // ── Uitgaven ──
    { id: 'wonen',         label: 'Wonen',               emoji: '🏠', color: '#5c6bc0', scope: 'uit' },
    { id: 'nutsvoorz',     label: 'Nutsvoorzieningen',   emoji: '💡', color: '#f1c40f', scope: 'uit' },
    { id: 'verzekeringen', label: 'Verzekeringen',       emoji: '🛡️', color: '#8e44ad', scope: 'uit' },
    { id: 'vervoer',       label: 'Vervoer',             emoji: '🚗', color: '#16a1b5', scope: 'uit' },
    { id: 'gezondheid',    label: 'Gezondheid',          emoji: '💊', color: '#27ae60', scope: 'uit' },
    { id: 'huisdier',      label: 'Huisdier',            emoji: '🐕', color: '#d35400', scope: 'uit' },
    { id: 'abonn',         label: 'Abonnementen',        emoji: '🎟️', color: '#e91e8c', scope: 'uit' },
    { id: 'boodschappen',  label: 'Boodschappen',        emoji: '🛒', color: '#2a9d5a', scope: 'uit' },
    { id: 'uitgaan',       label: 'Uitgaan',             emoji: '🍻', color: '#3949ab', scope: 'uit' },
    { id: 'eten',          label: 'Eten bestellen',      emoji: '🍕', color: '#e67e22', scope: 'uit' },
    { id: 'kleding',       label: 'Kleding',             emoji: '👕', color: '#ec407a', scope: 'uit' },
    { id: 'hobby',         label: 'Hobby',               emoji: '🎨', color: '#9c27b0', scope: 'uit' },
    { id: 'goede_doelen',  label: 'Goede doelen',        emoji: '💝', color: '#ef5350', scope: 'uit' },
    { id: 'belasting_uit', label: 'Belastingen',         emoji: '🧾', color: '#7f8c8d', scope: 'uit' },
    { id: 'pensioen',      label: 'Pensioensparen',      emoji: '💰', color: '#f39c12', scope: 'uit' },
    { id: 'gezamenlijk',   label: 'Gezamenlijke kosten', emoji: '🤝', color: '#378ADD', scope: 'uit', locked: true },
    { id: 'cadeau',        label: 'Cadeau',              emoji: '🎁', color: '#fbc02d', scope: 'uit' },
    { id: 'onverwacht',    label: 'Onverwacht',          emoji: '😱', color: '#c0392b', scope: 'uit' },
    { id: 'andere_uit',    label: 'Andere',              emoji: '📦', color: '#7f8c8d', scope: 'uit', locked: true },
    { id: 'sparen',        label: 'Sparen',              emoji: '🐷', color: '#5c6bc0', scope: 'uit', locked: true },
    { id: 'extra_sparen',  label: 'Extra sparen',        emoji: '🐷', color: '#8e44ad', scope: 'uit', locked: true },
    { id: 'terugbet_uit',  label: 'Terugbetaling',       emoji: '↪️', color: '#4db6ac', scope: 'uit', locked: true },

    // ── Inkomsten ──
    { id: 'loon',         label: 'Loon',                    emoji: '💼', color: '#2a9d5a', scope: 'in' },
    { id: 'flexi',        label: 'Flexi-job',               emoji: '🧾', color: '#16a1b5', scope: 'in' },
    { id: 'maaltijdcheq', label: 'Maaltijdcheques',         emoji: '🍽️', color: '#e67e22', scope: 'in' },
    { id: 'terugbet',     label: 'Terugbetaling',           emoji: '↩️', color: '#26a69a', scope: 'in' },
    { id: 'belasting_in', label: 'Belastingen (teruggave)', emoji: '🏛️', color: '#7f8c8d', scope: 'in' },
    { id: 'extra_in',     label: 'Extra inkomst',           emoji: '💰', color: '#66bb6a', scope: 'in' },
    { id: 'cadeau_in',    label: 'Cadeau ontvangen',        emoji: '🎁', color: '#fbc02d', scope: 'in' },
    { id: 'rest_vorig',   label: 'Rest vorige maand',       emoji: '🔄', color: '#4db6ac', scope: 'in', locked: true },
    { id: 'uit_spaar',    label: 'Uit spaarpot',            emoji: '🐷', color: '#9b59b6', scope: 'in', locked: true },
    { id: 'andere_in',    label: 'Andere',                  emoji: '📦', color: '#7f8c8d', scope: 'in', locked: true }
  ];
}
